import type { ChangeEvent } from 'react';
import { useState, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBeads } from '@/lib/api';
import { useImageWorker } from '@/lib/image-import';
import type { BeadColor, PipelineResult } from '@/lib/image-import';

type WizardStep = 'upload' | 'settings' | 'processing' | 'preview';

interface ImportWizardProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onApply: (result: PipelineResult) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function ImportWizard({ isOpen, onClose, onApply }: ImportWizardProps) {
  const [step, setStep] = useState<WizardStep>('upload');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageData, setImageData] = useState<ImageData | null>(null);
  const [targetWidth, setTargetWidth] = useState(30);
  const [numColors, setNumColors] = useState(10);
  const [useDithering, setUseDithering] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { progress, result, error: processingError, processImage, reset } = useImageWorker();

  // Fetch all beads for color matching
  const { data: beadsData } = useQuery({
    queryKey: ['beads-all'],
    queryFn: () => fetchBeads({ limit: 200 }),
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });

  const beadColors: BeadColor[] = (beadsData?.data ?? []).map((b) => ({
    id: b.id,
    code: b.code,
    hex: b.colorHex,
    lab: b.colorLab,
  }));

  // ─── Upload handler ────────────────────────────────

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    if (!file.type.startsWith('image/')) {
      setUploadError('Wybierz plik graficzny (PNG, JPG, WebP)');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Plik jest za duży (max 10MB)');
      return;
    }

    setImageFile(file);
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);

    // Load into canvas to get ImageData
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, img.width, img.height);
      setImageData(data);
      setStep('settings');
    };
    img.src = url;
  }, []);

  // ─── Process handler ───────────────────────────────

  const handleProcess = useCallback(() => {
    if (!imageData || beadColors.length === 0) return;

    setStep('processing');
    processImage(imageData, {
      targetWidth,
      numColors,
      useDithering,
      beadColors,
    });
  }, [imageData, targetWidth, numColors, useDithering, beadColors, processImage]);

  // Watch for result
  if (result && step === 'processing') {
    setStep('preview');
  }

  // ─── Apply handler ─────────────────────────────────

  const handleApply = useCallback(() => {
    if (!result) return;
    onApply(result);
    handleReset();
  }, [result, onApply]);

  const handleReset = useCallback(() => {
    setStep('upload');
    setImageFile(null);
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImagePreviewUrl(null);
    setImageData(null);
    setUploadError(null);
    reset();
  }, [imagePreviewUrl, reset]);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  if (!isOpen) return null;

  // Estimated height for display
  const aspectCorrection = 1.3;
  const estimatedHeight = imageData
    ? Math.round((targetWidth * imageData.height) / (imageData.width * aspectCorrection))
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="mx-4 flex max-h-[85dvh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-base font-semibold text-gray-900">Import zdjęcia</h2>
          <button onClick={handleClose} className="text-sm text-gray-400 hover:text-gray-600">
            Zamknij
          </button>
        </div>

        {/* Hidden canvas for image loading */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* ─── UPLOAD ─────────────────────── */}
          {step === 'upload' && (
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-40 w-full items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50">
                <label className="cursor-pointer text-center">
                  <div className="mb-2 text-3xl text-gray-300">📷</div>
                  <p className="text-sm font-medium text-primary-600">Wybierz zdjęcie</p>
                  <p className="text-xs text-gray-400">PNG, JPG, WebP (max 10MB)</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>
              {uploadError && (
                <p className="text-sm text-red-500">{uploadError}</p>
              )}
            </div>
          )}

          {/* ─── SETTINGS ───────────────────── */}
          {step === 'settings' && (
            <div className="flex flex-col gap-4">
              {/* Preview */}
              {imagePreviewUrl && (
                <div className="flex justify-center">
                  <img
                    src={imagePreviewUrl}
                    alt="Podgląd"
                    className="max-h-40 max-w-full rounded-lg border border-gray-200 object-contain"
                  />
                </div>
              )}

              <p className="text-center text-xs text-gray-400">
                {imageFile?.name} · {imageData?.width}×{imageData?.height}px
              </p>

              {/* Target width */}
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">
                  Szerokość wzoru (koraliki): {targetWidth}
                </span>
                <input
                  type="range"
                  min={10}
                  max={100}
                  value={targetWidth}
                  onChange={(e) => setTargetWidth(parseInt(e.target.value, 10))}
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] text-gray-400">
                  <span>10</span>
                  <span>Wynik: {targetWidth}×{estimatedHeight}</span>
                  <span>100</span>
                </div>
              </label>

              {/* Num colors */}
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">
                  Liczba kolorów: {numColors}
                </span>
                <input
                  type="range"
                  min={2}
                  max={25}
                  value={numColors}
                  onChange={(e) => setNumColors(parseInt(e.target.value, 10))}
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] text-gray-400">
                  <span>2</span>
                  <span>25</span>
                </div>
              </label>

              {/* Dithering toggle */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={useDithering}
                  onChange={(e) => setUseDithering(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Floyd-Steinberg dithering</span>
                <span className="text-[10px] text-gray-400">(gładsze przejścia kolorów)</span>
              </label>

              {/* Bead count info */}
              <p className="text-xs text-gray-400">
                Dostępne koraliki: {beadColors.length} · Dopasowanie CIEDE2000
              </p>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={handleReset}
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600"
                >
                  Zmień zdjęcie
                </button>
                <button
                  onClick={handleProcess}
                  disabled={beadColors.length === 0}
                  className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Konwertuj
                </button>
              </div>
            </div>
          )}

          {/* ─── PROCESSING ─────────────────── */}
          {step === 'processing' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
              <p className="text-sm font-medium text-gray-700">
                {progress?.stage ?? 'Przetwarzam...'}
              </p>
              {progress && (
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className="h-2 rounded-full bg-primary-600 transition-all"
                    style={{ width: `${Math.round(progress.progress * 100)}%` }}
                  />
                </div>
              )}
              {processingError && (
                <p className="text-sm text-red-500">{processingError}</p>
              )}
            </div>
          )}

          {/* ─── PREVIEW ────────────────────── */}
          {step === 'preview' && result && (
            <div className="flex flex-col gap-4">
              <div className="flex gap-3">
                {/* Before */}
                {imagePreviewUrl && (
                  <div className="flex-1">
                    <p className="mb-1 text-center text-[10px] font-medium text-gray-500">Oryginał</p>
                    <img
                      src={imagePreviewUrl}
                      alt="Oryginał"
                      className="w-full rounded-lg border border-gray-200 object-contain"
                    />
                  </div>
                )}

                {/* After (rendered from result) */}
                <div className="flex-1">
                  <p className="mb-1 text-center text-[10px] font-medium text-gray-500">
                    Wzór ({result.width}×{result.height})
                  </p>
                  <PatternPreview result={result} />
                </div>
              </div>

              {/* Palette */}
              <div>
                <p className="mb-1 text-[10px] font-medium text-gray-500">
                  Paleta ({result.palette.length} kolorów, {result.palette.reduce((s, p) => s + p.count, 0)} koralików)
                </p>
                <div className="flex flex-wrap gap-1">
                  {result.palette.map((p) => (
                    <div
                      key={p.index}
                      className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1"
                    >
                      <div
                        className="h-4 w-4 rounded border border-gray-300"
                        style={{ backgroundColor: p.beadHex }}
                      />
                      <span className="text-[10px] font-medium">{p.label}</span>
                      <span className="text-[10px] text-gray-400">{p.beadCode}</span>
                      <span className="text-[10px] text-gray-400">×{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => setStep('settings')}
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600"
                >
                  Zmień ustawienia
                </button>
                <button
                  onClick={handleApply}
                  className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Zastosuj wzór
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Simple pixel-grid preview rendered via canvas */
function PatternPreview({ result }: { result: PipelineResult }) {
  const canvasRef = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = result.width;
      canvas.height = result.height;

      for (let y = 0; y < result.height; y++) {
        const row = result.grid[y];
        if (!row) continue;
        for (let x = 0; x < result.width; x++) {
          const paletteIdx = row[x];
          if (paletteIdx === undefined || paletteIdx === 0) continue;
          const color = result.palette[paletteIdx - 1];
          if (color) {
            ctx.fillStyle = color.beadHex;
            ctx.fillRect(x, y, 1, 1);
          }
        }
      }
    },
    [result],
  );

  return (
    <canvas
      ref={canvasRef}
      className="w-full rounded-lg border border-gray-200"
      style={{ imageRendering: 'pixelated' }}
    />
  );
}
